import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  TableSortLabel,
  TextField,
  MenuItem,
  Checkbox,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  DragIndicator as DragIcon,
  SwapHoriz as StatusIcon,
  Receipt as ReceiptIcon,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "left" | "center";
  format?: (value: any, row: any) => React.ReactNode;
  sortable?: boolean;
  draggable?: boolean; // New property to control if column can be dragged
}

interface EnhancedTableProps {
  columns: Column[];
  data: any[];
  loading?: boolean;
  onEdit?: (row: any) => void;
  onDelete?: (row: any) => void;
  onView?: (row: any) => void;
  onStatusChange?: (row: any) => void;
  showActions?: boolean;
  actionsWidth?: number;
  emptyMessage?: string;
  /** Short entity name used to build default empty message, e.g. 'product', 'order' */
  entityName?: string;
  showViewCondition?: (row: any) => boolean;
  showStatusChangeCondition?: (row: any) => boolean;
  // Column reordering props
  enableColumnReordering?: boolean;
  onColumnReorder?: (newColumns: Column[]) => void;
  // Pagination props
  pagination?: {
    page: number;
    items_per_page: number;
    total: number;
  };
  onPageChange?: (newPage: number) => void;
  onItemsPerPageChange?: (newItemsPerPage: number) => void;
  useExternalPagination?: boolean;
  // Sorting props
  onSort?: (field: string) => void;
  useExternalSorting?: boolean;
  currentSortField?: string;
  currentSortDirection?: "asc" | "desc";
  // Column filter props
  columnFilters?: { [key: string]: string };
  onColumnFilterChange?: (columnId: string, value: string) => void;
  // Status filter options per column
  statusFilterOptions?: {
    [columnId: string]: { value: string; label: string }[];
  };
  // Row selection
  onSelectionChange?: (selectedRows: any[]) => void;
  showSelection?: boolean;
  // NEW optional enhancements
  enableStickyFilters?: boolean;
  onInvoiceClick?: (row: any) => void; // <-- add this line
}

const ACTION_ICON_SX = {
  width: 34,
  height: 34,
  borderRadius: "12px",
  bgcolor: "grey.50",
  border: "1px solid",
  borderColor: "grey.200",
  color: "text.secondary",
  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  transition: "all .18s ease",
  "&:hover": {
    bgcolor: "primary.main",
    borderColor: "primary.main",
    color: "#fff",
    boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
    transform: "translateY(-2px)",
  },
  "&:active": {
    transform: "translateY(0)",
    boxShadow: "0 2px 6px rgba(0,0,0,0.16)",
  },
};

const TRUNCATE_AT = 24;

const EnhancedTable: React.FC<EnhancedTableProps> = ({
  columns,
  data,
  loading = false,
  onEdit,
  onDelete,
  onView,
  onStatusChange,
  showActions = true,
  actionsWidth = 150,
  emptyMessage = "No data available",
  entityName = "",
  showViewCondition,
  showStatusChangeCondition,
  enableColumnReordering = false,
  onColumnReorder,
  pagination,
  onPageChange,
  onItemsPerPageChange,
  useExternalPagination = false,
  onSort,
  useExternalSorting = false,
  currentSortField = "",
  currentSortDirection = "asc",
  columnFilters = {},
  onColumnFilterChange,
  statusFilterOptions = {},
  onSelectionChange,
  showSelection = true,
  enableStickyFilters = true,
  onInvoiceClick,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<string>("");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [selected, setSelected] = useState<any[]>([]);
  // Selection logic
  const isRowSelected = (row: any) => {
    return selected.some((sel) => (row.id ? sel.id === row.id : sel === row));
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(paginatedData);
      if (onSelectionChange) onSelectionChange(paginatedData);
    } else {
      setSelected([]);
      if (onSelectionChange) onSelectionChange([]);
    }
  };

  const handleRowClick = (
    event: React.ChangeEvent<HTMLInputElement>,
    row: any
  ) => {
    let newSelected;
    if (isRowSelected(row)) {
      newSelected = selected.filter((sel) =>
        row.id ? sel.id !== row.id : sel !== row
      );
    } else {
      newSelected = [...selected, row];
    }
    setSelected(newSelected);
    if (onSelectionChange) onSelectionChange(newSelected);
  };

  // Drag and drop state
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);
  const [localColumns, setLocalColumns] = useState<Column[]>(columns);

  // Update local columns when props change
  React.useEffect(() => {
    setLocalColumns(columns);
  }, [columns]);

  const handleChangePage = (event: unknown, newPage: number) => {
    if (useExternalPagination && onPageChange) {
      onPageChange(newPage + 1); // Convert to 1-based indexing for API
    } else {
      setPage(newPage);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = +event.target.value;
    if (useExternalPagination && onItemsPerPageChange) {
      onItemsPerPageChange(newRowsPerPage);
    } else {
      setRowsPerPage(newRowsPerPage);
      setPage(0);
    }
  };

  const handleSort = (property: string) => {
    if (useExternalSorting && onSort) {
      // Use external sorting
      onSort(property);
    } else {
      // Use internal sorting
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, columnId: string) => {
    if (!enableColumnReordering) return;

    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", columnId);
  };

  const handleDragOver = (e: React.DragEvent, columnId: string) => {
    if (!enableColumnReordering || !draggedColumn || draggedColumn === columnId)
      return;

    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverColumn(columnId);
  };

  const handleDrop = (e: React.DragEvent, targetColumnId: string) => {
    if (
      !enableColumnReordering ||
      !draggedColumn ||
      draggedColumn === targetColumnId
    )
      return;

    e.preventDefault();

    const draggedIndex = localColumns.findIndex(
      (col) => col.id === draggedColumn
    );
    const targetIndex = localColumns.findIndex(
      (col) => col.id === targetColumnId
    );

    if (draggedIndex === -1 || targetIndex === -1) return;

    // Create new column order
    const newColumns = [...localColumns];
    const [draggedColumnData] = newColumns.splice(draggedIndex, 1);
    newColumns.splice(targetIndex, 0, draggedColumnData);

    // Update local state
    setLocalColumns(newColumns);

    // Notify parent component
    if (onColumnReorder) {
      onColumnReorder(newColumns);
    }

    // Reset drag state
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  const sortedData = React.useMemo(() => {
    if (useExternalSorting) {
      // When using external sorting, return data as-is since it's already sorted by the API
      return Array.isArray(data) ? data : [];
    }

    if (!orderBy) return Array.isArray(data) ? data : [];

    const dataArray = Array.isArray(data) ? data : [];
    return [...dataArray].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        return order === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }, [data, orderBy, order, useExternalSorting]);

  // Ensure sortedData is always an array
  const safeSortedData = Array.isArray(sortedData) ? sortedData : [];
  // If using external pagination, show all data as received (already paginated by API)
  const paginatedData = useExternalPagination
    ? safeSortedData
    : safeSortedData.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      );

  const effectiveEmptyMessage = React.useMemo(() => {
    // If a custom emptyMessage was passed (and it's not the generic default), use it.
    if (emptyMessage && emptyMessage !== "No data available") return emptyMessage;
    // Otherwise, if entityName provided, build a default message using it.
    if (entityName) return `No ${entityName} data found`;
    return "No records found";
  }, [emptyMessage, entityName]);

  const renderCell = (column: Column, row: any) => {
    if (column.format) {
      return column.format(row[column.id], row);
    }

    const value = row[column.id];
    if (value === null || value === undefined)
      return (
        <Typography variant="body2" color="text.disabled">
          -
        </Typography>
      );

    if (typeof value === "boolean") {
      return (
        <Chip
          label={value ? "Active" : "Inactive"}
          size="small"
          sx={{
            fontWeight: 600,
            letterSpacing: 0.3,
            bgcolor: value
              ? (theme) => alpha(theme.palette.success.main, 0.12)
              : (theme) => alpha(theme.palette.error.main, 0.12),
            color: value ? "success.dark" : "error.dark",
            borderRadius: "8px",
            px: 0.5,
          }}
        />
      );
    }

    if (typeof value === "string") {
      const showTooltip = value.length > TRUNCATE_AT;
      const display = showTooltip ? value.slice(0, TRUNCATE_AT) + "…" : value;
      return (
        <Tooltip title={showTooltip ? value : ""} arrow disableInteractive>
          <Typography
            variant="body2"
            noWrap
            sx={{ maxWidth: 220, fontWeight: 500 }}
          >
            {display}
          </Typography>
        </Tooltip>
      );
    }

    return (
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {String(value)}
      </Typography>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 4,
        border: "1px solid",
        borderColor: "grey.200",
        background: "#fff", // unified solid background
        position: "relative",
        overflow: "hidden",
        boxShadow: "0 4px 24px -6px rgba(0,0,0,0.08)",
        // removed gradient & radial overlay to avoid differing shades
      }}
    >
      <TableContainer
        sx={{
          borderRadius: 0,
          background: "#fff",
          // removed maxHeight to prevent internal scroll for default 10 rows
          "&::-webkit-scrollbar": { width: 10, height: 10 },
          "&::-webkit-scrollbar-track": {
            background: "rgba(0,0,0,0.04)",
            borderRadius: 8,
          },
          "&::-webkit-scrollbar-thumb": {
            background: (t) => alpha(t.palette.primary.main, 0.3),
            borderRadius: 8,
            border: "2px solid transparent",
            backgroundClip: "content-box",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: (t) => alpha(t.palette.primary.main, 0.55),
          },
          px: 0,
        }}
      >
        <Table
          stickyHeader
          sx={{
            minWidth: 650,
            borderCollapse: "separate",
            borderSpacing: 0,
            background: "#fff", // ensure table base is white
            "& thead th": {
              bgcolor: "#fff",
              fontSize: 13.5, // unified header font size
              fontWeight: 700,
              lineHeight: 1.3,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              color: "text.primary", // unified color
              borderBottom: "1px solid",
              borderColor: "grey.200",
              position: "sticky",
              top: 0,
              zIndex: 3,
              backdropFilter: "blur(6px)",
              "&:first-of-type": { pl: 3, borderTopLeftRadius: 16 },
              "&:last-of-type": { pr: 3, borderTopRightRadius: 16 },
            },
            "& thead tr": {
              background: "#fff",
            },
            "& tbody": { background: "#fff" },
            "& tbody tr": {
              transition: "all .25s cubic-bezier(.4,0,.2,1)",
              "&:nth-of-type(odd)": {
                bgcolor: (t) => alpha(t.palette.primary.light, 0.03),
              },
              "&:hover": {
                bgcolor: "background.paper",
                boxShadow: "0 6px 14px -4px rgba(0,0,0,0.12)",
                transform: "translateY(-2px)",
              },
              "&.Mui-selected": {
                bgcolor: (t) =>
                  alpha(t.palette.primary.main, 0.1) + " !important",
                "&:hover": {
                  bgcolor: (t) =>
                    alpha(t.palette.primary.main, 0.14) + " !important",
                },
              },
              "& td": {
                borderBottom: "1px solid",
                borderColor: "grey.100",
                fontSize: 13,
                fontWeight: 500,
              },
              "& td:first-of-type": { pl: 3 },
              "& td:last-of-type": { pr: 3 },
            },
          }}
          aria-label="enhanced table"
        >
          <TableHead>
            <TableRow
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 4,
                background: "inherit",
              }}
            >
              {showSelection && onSelectionChange && (
                <TableCell padding="checkbox" sx={{ backgroundColor: "white" }}>
                  <Checkbox
                    color="primary"
                    indeterminate={
                      selected.length > 0 &&
                      selected.length < paginatedData.length
                    }
                    checked={
                      paginatedData.length > 0 &&
                      selected.length === paginatedData.length
                    }
                    onChange={handleSelectAllClick}
                    inputProps={{ "aria-label": "select all rows" }}
                  />
                </TableCell>
              )}
              {localColumns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || "center"}
                  draggable={
                    enableColumnReordering && column.draggable !== false
                  }
                  onDragStart={(e) => handleDragStart(e, column.id)}
                  onDragOver={(e) => handleDragOver(e, column.id)}
                  onDrop={(e) => handleDrop(e, column.id)}
                  onDragEnd={handleDragEnd}
                  style={{
                    minWidth: column.minWidth,
                    backgroundColor: "white",
                    color: "text.primary",
                    fontWeight: 600,
                    cursor:
                      enableColumnReordering && column.draggable !== false
                        ? "grab"
                        : "default",
                    opacity: draggedColumn === column.id ? 0.5 : 1,
                    borderLeft:
                      dragOverColumn === column.id
                        ? "3px solid #1976d2"
                        : "none",
                    borderRight:
                      dragOverColumn === column.id
                        ? "3px solid #1976d2"
                        : "none",
                    transition: "all 0.2s ease",
                  }}
                  sx={{
                    "&:hover": {
                      backgroundColor:
                        enableColumnReordering && column.draggable !== false
                          ? "grey.200"
                          : "white",
                    },
                    "&:active": {
                      cursor: "grabbing",
                    },
                  }}
                >
                  {(() => {
                    const isFiltered = !!(
                      columnFilters &&
                      Object.prototype.hasOwnProperty.call(
                        columnFilters,
                        column.id
                      ) &&
                      columnFilters[column.id] &&
                      columnFilters[column.id] !== "" &&
                      columnFilters[column.id] !== "all"
                    );
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          justifyContent: "center",
                          position: "relative",
                        }}
                      >
                        {enableColumnReordering &&
                          column.draggable !== false && (
                            <DragIcon
                              sx={{
                                fontSize: "1rem",
                                color: "text.secondary",
                                cursor: "grab",
                                "&:active": {
                                  cursor: "grabbing",
                                },
                              }}
                            />
                          )}
                        {column.sortable ? (
                          <TableSortLabel
                            active={
                              useExternalSorting
                                ? currentSortField === column.id
                                : orderBy === column.id
                            }
                            direction={
                              useExternalSorting
                                ? currentSortDirection
                                : orderBy === column.id
                                ? order
                                : "asc"
                            }
                            onClick={() => handleSort(column.id)}
                            sx={{
                              color: "text.primary",
                              "&.MuiTableSortLabel-active": {
                                color: "primary.main",
                              },
                              "& .MuiTableSortLabel-icon": {
                                color: "text.secondary !important",
                              },
                              "&.MuiTableSortLabel-active .MuiTableSortLabel-icon":
                                {
                                  color: "primary.main !important",
                                },
                            }}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {column.label}
                          </Typography>
                        )}
                        {isFiltered && (
                          <Box
                            component="span"
                            sx={{
                              ml: 0.5,
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: "primary.main",
                              boxShadow: "0 0 0 2px #fff",
                            }}
                            aria-label="filter active"
                          />
                        )}
                      </Box>
                    );
                  })()}
                </TableCell>
              ))}
              {showActions && (
                <TableCell
                  align="center"
                  style={{
                    minWidth: actionsWidth,
                    backgroundColor: "white",
                    color: "text.primary",
                    fontWeight: 600,
                  }}
                >
                  Actions
                </TableCell>
              )}
            </TableRow>

            {/* Generic Filter Row - enhanced styling */}
            <TableRow
              sx={
                enableStickyFilters
                  ? {
                      position: "sticky",
                      top: 56,
                      zIndex: 3,
                      background: "#fff",
                      boxShadow: "0 1px 0 0 rgba(0,0,0,0.08)",
                      "&:after": { display: "none" },
                    }
                  : undefined
              }
            >
              {showSelection && onSelectionChange && (
                <TableCell sx={{ backgroundColor: "#fff", py: 1.2, pl: 3 }} />
              )}
              {localColumns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align || "center"}
                  sx={{ backgroundColor: "#fff", py: 1.2 }}
                >
                  {columnFilters &&
                  Object.prototype.hasOwnProperty.call(
                    columnFilters,
                    column.id
                  ) ? (
                    column.id.toLowerCase() === "date" ? (
                      <TextField
                        type="date"
                        value={columnFilters[column.id] || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onColumnFilterChange &&
                          onColumnFilterChange(column.id, e.target.value)
                        }
                        size="small"
                        fullWidth
                        variant="outlined"
                        inputProps={{ max: new Date().toISOString().split("T")[0] }}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            bgcolor: "grey.50",
                            "& fieldset": { borderColor: "grey.200" },
                            "&:hover fieldset": {
                              borderColor: "primary.light",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "primary.main",
                              boxShadow: (theme) =>
                                `0 0 0 2px ${alpha(
                                  theme.palette.primary.main,
                                  0.15
                                )}`,
                            },
                          },
                          minWidth: 80,
                        }}
                      />
                    ) : column.id.toLowerCase().includes("status") ? (
                      <TextField
                        select
                        value={
                          columnFilters[column.id] ||
                          statusFilterOptions[column.id]?.[0]?.value ||
                          "all"
                        }
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onColumnFilterChange &&
                          onColumnFilterChange(column.id, e.target.value)
                        }
                        size="small"
                        fullWidth
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            bgcolor: "grey.50",
                            "& fieldset": { borderColor: "grey.200" },
                            "&:hover fieldset": {
                              borderColor: "primary.light",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "primary.main",
                              boxShadow: (theme) =>
                                `0 0 0 2px ${alpha(
                                  theme.palette.primary.main,
                                  0.15
                                )}`,
                            },
                          },
                          minWidth: 80,
                        }}
                      >
                        {(
                          statusFilterOptions[column.id] || [
                            { value: "all", label: "All" },
                            { value: "active", label: "Active" },
                            { value: "inactive", label: "Inactive" },
                          ]
                        ).map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : typeof columnFilters[column.id] === "number" ||
                      column.id.toLowerCase().includes("quantity") ||
                      column.id.toLowerCase().includes("points") ? (
                      <TextField
                        value={columnFilters[column.id] || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onColumnFilterChange &&
                          onColumnFilterChange(column.id, e.target.value)
                        }
                        placeholder={`Search ${column.label}`}
                        size="small"
                        fullWidth
                        variant="outlined"
                        type="number"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            bgcolor: "grey.50",
                            "& fieldset": { borderColor: "grey.200" },
                            "&:hover fieldset": {
                              borderColor: "primary.light",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "primary.main",
                              boxShadow: (theme) =>
                                `0 0 0 2px ${alpha(
                                  theme.palette.primary.main,
                                  0.15
                                )}`,
                            },
                          },
                          minWidth: 80,
                        }}
                      />
                    ) : (
                      <TextField
                        value={columnFilters[column.id] || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          onColumnFilterChange &&
                          onColumnFilterChange(column.id, e.target.value)
                        }
                        placeholder={`Search ${column.label}`}
                        size="small"
                        fullWidth
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            bgcolor: "grey.50",
                            "& fieldset": { borderColor: "grey.200" },
                            "&:hover fieldset": {
                              borderColor: "primary.light",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "primary.main",
                              boxShadow: (theme) =>
                                `0 0 0 2px ${alpha(
                                  theme.palette.primary.main,
                                  0.15
                                )}`,
                            },
                          },
                          minWidth: 80,
                        }}
                      />
                    )
                  ) : null}
                </TableCell>
              ))}
              {showActions && (
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "#fff",
                    py: 1.2,
                    position: "relative",
                  }}
                />
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={localColumns.length + (showActions ? 1 : 0)}
                  align="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    Loading...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={localColumns.length + (showActions ? 1 : 0)}
                  align="center"
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1.25,
                      py: 6,
                      px: 2,
                      // small floating animation for SVG elements
                      "@keyframes floatY": {
                        "0%": { transform: "translateY(0px)" },
                        "50%": { transform: "translateY(-8px)" },
                        "100%": { transform: "translateY(0px)" },
                      },
                      "@keyframes floatRotate": {
                        "0%": { transform: "rotate(0deg)" },
                        "50%": { transform: "rotate(6deg)" },
                        "100%": { transform: "rotate(0deg)" },
                      },
                    }}
                  >
                    <Box
                      component="svg"
                      viewBox="0 0 160 110"
                      sx={{
                        width: 160,
                        height: 110,
                        overflow: "visible",
                        // more vibrant, complementary colors
                        "& .bg": { fill: "#F3F8FF" }, // very light blue background
                        "& .accent": { fill: "#06B6D4" }, // cyan accent
                        "& .dot": {
                          animation: "floatY 2200ms ease-in-out infinite",
                        },
                        "& .paper": {
                          fill: "#FFFFFF",
                          stroke: "#E6EEF7",
                          strokeWidth: 1,
                          boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                          transformOrigin: "80px 55px",
                          animation: "floatRotate 4000ms ease-in-out infinite",
                        },
                      }}
                    >
                      <rect className="bg" x="0" y="0" width="160" height="110" rx="8" />
                      <g transform="translate(28,18)">
                        <rect className="paper" x="0" y="6" width="104" height="70" rx="8" />
                        <rect x="10" y="16" width="84" height="10" rx="4" className="accent" opacity="0.18" />
                        <rect x="10" y="34" width="64" height="8" rx="4" fill="#EFFAF6" />
                        <circle className="dot" cx="88" cy="28" r="6" fill="#7C3AED" opacity={1} />
                        <circle className="dot" cx="98" cy="54" r="4" fill="#06B6D4" opacity={0.9} />
                        <circle className="dot" cx="18" cy="72" r="5" fill="#F97316" opacity={0.85} />
                      </g>
                    </Box>

                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ mt: 1, textTransform: "none", color: "primary.main" }}
                    >
                      {effectiveEmptyMessage || "No records found"}
                    </Typography>

                    {entityName ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Try adjusting filters or add a new {entityName}.
                      </Typography>
                    ) : null}
                  </Box>
                </TableCell>
              </TableRow>
             ) : (
              paginatedData.map((row, index) => (
                <TableRow
                  key={row.id || index}
                  hover
                  sx={{
                    backgroundColor: "white",
                    "&:hover": {
                      backgroundColor: "action.selected",
                    },
                  }}
                  selected={isRowSelected(row)}
                >
                  {showSelection && onSelectionChange && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isRowSelected(row)}
                        onChange={(event) => handleRowClick(event, row)}
                        inputProps={{
                          "aria-label": `select row ${row.id || index}`,
                        }}
                      />
                    </TableCell>
                  )}
                  {localColumns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align || "center"}
                      sx={{
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        py: 1.5,
                      }}
                    >
                      {renderCell(column, row)}
                    </TableCell>
                  ))}
                  {showActions && (
                    <TableCell
                      align="center"
                      sx={{
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        py: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          justifyContent: "center",
                        }}
                      >
                        {onView &&
                          (!showViewCondition || showViewCondition(row)) && (
                            <Tooltip title="View">
                              <IconButton
                                size="small"
                                onClick={() => onView(row)}
                                sx={ACTION_ICON_SX}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        {onEdit && (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => onEdit(row)}
                              sx={{
                                ...ACTION_ICON_SX,
                                "&:hover": {
                                  ...ACTION_ICON_SX["&:hover"],
                                  bgcolor: "secondary.main",
                                  borderColor: "secondary.main",
                                },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onDelete && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => onDelete(row)}
                              sx={{
                                ...ACTION_ICON_SX,
                                "&:hover": {
                                  ...ACTION_ICON_SX["&:hover"],
                                  bgcolor: "error.main",
                                  borderColor: "error.main",
                                },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onStatusChange &&
                          showStatusChangeCondition &&
                          showStatusChangeCondition(row) && (
                            <Tooltip title="Change Status">
                              <IconButton
                                size="small"
                                onClick={() => onStatusChange(row)}
                                sx={{
                                  ...ACTION_ICON_SX,
                                  "&:hover": {
                                    ...ACTION_ICON_SX["&:hover"],
                                    bgcolor: "info.main",
                                    borderColor: "info.main",
                                  },
                                }}
                              >
                                <StatusIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                        )}
                        {onInvoiceClick && (
                          <Tooltip title="Invoice">
                            <IconButton
                              size="small"
                              onClick={() => onInvoiceClick(row)}
                              sx={ACTION_ICON_SX}
                            >
                              <ReceiptIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50,100]}
        component="div"
        count={
          useExternalPagination && pagination
            ? pagination.total
            : sortedData.length
        }
        rowsPerPage={
          useExternalPagination && pagination
            ? pagination.items_per_page
            : rowsPerPage
        }
        page={useExternalPagination && pagination ? pagination.page - 1 : page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          px: 2,
          py: 1.25,
          borderTop: "1px solid",
          borderColor: "grey.200",
          bgcolor: "#fff",
          "& .MuiTablePagination-toolbar": {
            width: "100%",
            justifyContent: "flex-end",
            gap: 2,
            flexWrap: "nowrap",
            minHeight: 48,
            py: 0,
          },
          "& .MuiTablePagination-actions": {
            ml: 1,
            mr: 2, // <-- add this line for right margin
          },
          "& .MuiTablePagination-actions .MuiIconButton-root": {
            width: 30,
            height: 30,
            borderRadius: "10px",
            border: "1px solid",
            borderColor: "grey.200",
            bgcolor: "grey.50",
            transition: "all .18s ease",
            "&:hover": {
              bgcolor: "primary.main",
              color: "#fff",
              borderColor: "primary.main",
            },
          },
        }}
      />
    </Paper>
  );
};

export default EnhancedTable;
